/**
 * ObservabilityPanel — MCP + Agent telemetry dashboard.
 * Shows Activity feed, Stats, and Security tabs.
 * Polls /cp/observability/* endpoints.
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import {
  Activity, BarChart3, Shield, Clock, CheckCircle2, XCircle,
  AlertTriangle, ChevronDown, ChevronRight, Copy, RefreshCw,
} from 'lucide-react';

const CP_BASE = import.meta.env.VITE_CP_BASE || '/cp';

function getHeaders() {
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  try {
    const s = useAuthStore.getState();
    if (s?.token) h['Authorization'] = `Bearer ${s.token}`;
    if (s?.selectedTenant) h['x-tenant-id'] = s.selectedTenant;
  } catch { /* ignore */ }
  return h;
}

async function fetchJson<T>(path: string): Promise<T> {
  const res = await fetch(`${CP_BASE}${path}`, { headers: getHeaders() });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

interface ObservabilityPanelProps {
  appId: string;
}

export default function ObservabilityPanel({ appId }: ObservabilityPanelProps) {
  const [tab, setTab] = useState<'activity' | 'stats' | 'security'>('activity');
  const [expanded, setExpanded] = useState<string | null>(null);

  const qRecent = useQuery({
    queryKey: ['observability', 'recent', appId],
    queryFn: () => fetchJson<any>(`/observability/recent/${appId}`),
    refetchInterval: 5000,
    enabled: Boolean(appId),
  });

  const qStats = useQuery({
    queryKey: ['observability', 'stats', appId],
    queryFn: () => fetchJson<any>(`/observability/stats/${appId}`),
    refetchInterval: 30000,
    enabled: Boolean(appId) && tab === 'stats',
  });

  const qSecurity = useQuery({
    queryKey: ['observability', 'security', appId],
    queryFn: () => fetchJson<any>(`/observability/security/${appId}`),
    refetchInterval: 30000,
    enabled: Boolean(appId) && tab === 'security',
  });

  const tabs = [
    { id: 'activity' as const, label: 'Activity', icon: Activity },
    { id: 'stats' as const, label: 'Stats', icon: BarChart3 },
    { id: 'security' as const, label: 'Security', icon: Shield },
  ];

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
      {/* Header + Tabs */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-gray-800">MCP Observability</h3>
        <div className="flex gap-1">
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                tab === t.id ? 'bg-blue-50 text-blue-700' : 'text-gray-500 hover:bg-gray-50'
              }`}
            >
              <t.icon size={14} />
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-h-[400px] overflow-y-auto">
        {/* Activity Tab */}
        {tab === 'activity' && (
          <div className="p-3 space-y-1">
            {qRecent.isLoading && <p className="text-xs text-gray-400 p-2">Loading...</p>}
            {qRecent.data?.events?.length === 0 && (
              <p className="text-xs text-gray-400 p-4 text-center">No events yet. MCP calls will appear here in real-time.</p>
            )}
            {(qRecent.data?.events || []).map((ev: any) => (
              <div
                key={ev.event_id}
                className={`p-2 rounded border text-xs cursor-pointer transition-colors ${
                  !ev.success ? 'border-red-200 bg-red-50' :
                  ev.threat_flags?.length > 0 ? 'border-yellow-200 bg-yellow-50' :
                  'border-gray-100 hover:bg-gray-50'
                }`}
                onClick={() => setExpanded(expanded === ev.event_id ? null : ev.event_id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {ev.success ? (
                      <CheckCircle2 size={12} className="text-green-500" />
                    ) : (
                      <XCircle size={12} className="text-red-500" />
                    )}
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                      ev.source === 'mcp_gateway' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                    }`}>
                      {ev.source === 'mcp_gateway' ? 'MCP' : 'Public'}
                    </span>
                    <span className="font-mono text-gray-700">{ev.tool_name || ev.event_type}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-400">
                    {ev.duration_ms > 0 && <span>{(ev.duration_ms / 1000).toFixed(1)}s</span>}
                    <span>{new Date(ev.time).toLocaleTimeString()}</span>
                    {expanded === ev.event_id ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  </div>
                </div>
                {expanded === ev.event_id && (
                  <div className="mt-2 pt-2 border-t border-gray-100 space-y-1">
                    {ev.input_summary && (
                      <div>
                        <span className="text-gray-400">Input: </span>
                        <span className="text-gray-600">{ev.input_summary.slice(0, 300)}</span>
                      </div>
                    )}
                    {ev.error && (
                      <div className="text-red-600">Error: {ev.error}</div>
                    )}
                    {ev.threat_flags?.length > 0 && (
                      <div className="flex gap-1 flex-wrap">
                        {ev.threat_flags.map((f: string) => (
                          <span key={f} className="px-1.5 py-0.5 bg-yellow-100 text-yellow-800 rounded text-[10px]">{f}</span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Stats Tab */}
        {tab === 'stats' && (
          <div className="p-4 space-y-4">
            {qStats.isLoading && <p className="text-xs text-gray-400">Loading stats...</p>}
            {qStats.data?.summary && (
              <div className="grid grid-cols-4 gap-3">
                {[
                  { label: 'Total Calls', value: qStats.data.summary.total_calls || 0 },
                  { label: 'Successful', value: qStats.data.summary.successful || 0 },
                  { label: 'Failed', value: qStats.data.summary.failed || 0 },
                  { label: 'Avg Latency', value: `${qStats.data.summary.avg_duration_ms || 0}ms` },
                ].map(s => (
                  <div key={s.label} className="bg-gray-50 rounded p-3 text-center">
                    <div className="text-lg font-bold text-gray-800">{s.value}</div>
                    <div className="text-[10px] text-gray-500 uppercase">{s.label}</div>
                  </div>
                ))}
              </div>
            )}
            {qStats.data?.top_tools?.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-gray-600 mb-2">Top Tools (24h)</h4>
                <div className="space-y-1">
                  {qStats.data.top_tools.map((t: any) => (
                    <div key={t.tool_name} className="flex justify-between text-xs py-1 px-2 bg-gray-50 rounded">
                      <span className="font-mono text-gray-700">{t.tool_name}</span>
                      <span className="text-gray-500">{t.calls} calls, avg {t.avg_ms}ms</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Security Tab */}
        {tab === 'security' && (
          <div className="p-4 space-y-4">
            {qSecurity.isLoading && <p className="text-xs text-gray-400">Loading security data...</p>}
            {qSecurity.data?.summary && (
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-gray-50 rounded p-3 text-center">
                  <div className="text-lg font-bold text-gray-800">{qSecurity.data.summary.total}</div>
                  <div className="text-[10px] text-gray-500">TOTAL</div>
                </div>
                <div className={`rounded p-3 text-center ${qSecurity.data.summary.flagged > 0 ? 'bg-yellow-50' : 'bg-gray-50'}`}>
                  <div className={`text-lg font-bold ${qSecurity.data.summary.flagged > 0 ? 'text-yellow-700' : 'text-gray-800'}`}>
                    {qSecurity.data.summary.flagged}
                  </div>
                  <div className="text-[10px] text-gray-500">FLAGGED</div>
                </div>
                <div className={`rounded p-3 text-center ${qSecurity.data.summary.blocked > 0 ? 'bg-red-50' : 'bg-gray-50'}`}>
                  <div className={`text-lg font-bold ${qSecurity.data.summary.blocked > 0 ? 'text-red-700' : 'text-gray-800'}`}>
                    {qSecurity.data.summary.blocked}
                  </div>
                  <div className="text-[10px] text-gray-500">BLOCKED</div>
                </div>
              </div>
            )}
            {qSecurity.data?.flagged_events?.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-gray-600 mb-2">Recent Flagged Events</h4>
                {qSecurity.data.flagged_events.map((ev: any, i: number) => (
                  <div key={i} className="p-2 border border-yellow-200 bg-yellow-50 rounded text-xs mb-1">
                    <div className="flex justify-between">
                      <span className="font-mono">{ev.tool_name}</span>
                      <span className="text-gray-400">{new Date(ev.time).toLocaleTimeString()}</span>
                    </div>
                    <div className="flex gap-1 mt-1 flex-wrap">
                      {(ev.threat_flags || []).map((f: string) => (
                        <span key={f} className="px-1.5 py-0.5 bg-yellow-200 text-yellow-900 rounded text-[10px]">{f}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
